import { responsive, VStack, useResponsive, colors } from "goi_common";

import GuideHead from "./components/Guide/GuideHead";
import { Stack } from "@chakra-ui/react";
import GuideBox from "./components/Guide/GuideBox";
import GuideContent from "./components/Guide/GuideContent";
import GuideFooter from "./components/Guide/GuideFooter";
import Footer from "../Home/components/Footer";
import { useViewpostTopObserver } from "@/hooks/useViewpostTopObserver";
import LandingFixedButton from "@/components/common/LandingFixedButton";

export default function ContentSecondSnGuide() {
  const { isBiggerThanLg } = useResponsive();

  const { viewportTopRef } = useViewpostTopObserver();

  return (
    <VStack width="100%" alignItems="center">
      <div ref={viewportTopRef}></div>
      <GuideHead
        title="장례 가이드북 2편 - 임종 전"
        date="2024년 03월 26일 작성"
        imgSrc="platform/prepare/first-carousel-2.png"
      />
      <Stack
        maxWidth={`720px`}
        width="100%"
        css={responsive({
          gap: { base: "28px", md: "40px" },
          padding: { base: "0 0px", lg: "60px 20px" },
        })}
      >
        <GuideBox>
          해당 글은 고이 송슬옹 대표가 직접 작성한 모 커뮤니티 베스트 게시글의
          내용을 가져왔으며,
          {!isBiggerThanLg && <br />}
          <br />글 중간의 이미지 추가, 오타 수정 등으로 원글과 다른 내용이 있을
          수 있습니다.
        </GuideBox>

        <GuideContent contents={SECOND_SNU_CONTENT} />

        <GuideFooter />
      </Stack>
      <LandingFixedButton
        color={colors.brown650}
        text="고이와 함께 장례 준비하기"
        eventPath="content_sn_2"
      />
      <Footer />
    </VStack>
  );
}

const SECOND_SNU_CONTENT: Array<{
  type: "p" | "img" | "h2" | "h3" | "h4" | "button" | "a";
  resource: string;
  imgAlt?: string;
  onClick?: () => void;
  href?: string;
  newGtm?: {
    path: string;
    trigger_and_target: string;
  };
}> = [
  {
    type: "p",
    resource:
      "안녕하세요. 학부 출신 현직 장례지도사입니다. 3년 전에 장례 가이드북 1편 - 장례 전'을 쓴 뒤로 많은 분이 댓글을 남겨주시고, 스크랩을 해 주셨는데요, 접하기 힘들지만 언젠가는 알아야만 하는 정보이기 때문일 것 같습니다. 많은 분이 읽어주셔서 감사한 마음을 느꼈고, 덕분에 제가 하는 일에 더 큰 자부심을 가질 수 있게 되는 것 같습니다. 그래서 마음의 부채로 남아 있던 장례 가이드북 연재를 다시 시작해야겠다고 마음을 먹었습니다.",
  },
  {
    type: "a",
    resource: "장례 가이드북 1편 보러가기",
    href: "/content/guide/sn-1/",
    newGtm: {
      path: "content",
      trigger_and_target: "sn-2-uv_sn-1",
    },
  },
  {
    type: "p",
    resource:
      "장례 가이드북 1편에서는 미리 장례를 준비하는 막막한 때에 어떤 것을 알아보고 결정해야 하는지 가이드를 드리는 데 목표가 있었다면, <u>이번 편에서는 임종 전에 무엇을 준비해야 하는지, 임종 직후에 무엇을 어떻게 해야 하는지 실질적인 대처 방법</u>을 알려드리는 것을 목표로 합니다. 최초에는 장례 중의 모든 일을 2편에 담을 계획이었지만, 임종 전후의 가이드만 해도 많은 양이기 때문에 장례식 1일~3일의 절차를 다루는 내용은 다음 편에서 안내해 드리고자 합니다. 피할 수 없는 일이라면 미리 잘 알고 대처하시길 바라는 마음에서 글을 시작합니다.",
  },
  { type: "img", resource: "direct/content/sn/2-1.png", imgAlt: "임종 준비" },
  {
    type: "h2",
    resource: "임종 전",
  },
  {
    type: "p",
    resource:
      "<u>임종이 예상되는 상황에서는 고인의 신분증, 유언, 영정 사진 3가지를 꼭 챙겨두시길 권해드립니다.</u>",
  },
  { type: "h3", resource: "1. 고인의 신분증" },
  {
    type: "p",
    resource:
      "<u>고인의 장례 이후는 물론이고, 사망진단서를 발급할 때도 고인의 신분증이 꼭 필요합니다.</u> 사망진단서를 발급하기 위해서 고인의 주소 확인이 필요하기 때문에 최소한 고인의 신분증은 필요하며, 고인의 성함이 기재된 가족관계증명서 혹은 주민등록 등본을 요구하는 병원도 있습니다. 상급종합병원에는 무인민원발급기가 있기 때문에 괜찮을지 모르겠으나, 그렇지 않을 경우 모든 절차에 꼭 필요한 사망진단서를 발급하지 못해 난감한 상황을 겪는 분들이 있었습니다.",
  },
  {
    type: "img",
    resource: "direct/content/sn/2-2.png",
    imgAlt: "임종 직후 준비",
  },
  { type: "h3", resource: "2. 유언" },
  {
    type: "p",
    resource:
      "또한 희망하는 장례 방식, 각종 금융 및 자산 관련 정보, 유산 배분 방식 등이 포함된 유언을 챙겨주세요. 다만 현실적으로 법적 효력을 갖춘 유언을 준비해 두시는 분이 많지 않습니다. 그래서 저는 <u>유언을 준비하기 어려운 분들께는 '사전 장례 의향서'를 권해드립니다.</u> 법적 구속력은 없지만, 고인의 뜻을 기준으로 남은 가족 간의 의견 차이를 좁히는 데 큰 도움이 됩니다.\n\n어떤 종교에 따라 장례를 진행할지, 부고는 어느 범위까지 알릴지, 화장 후 어떤 장지로 가는 것이 좋은지 등의 질문이 포함된 문서이고, 포털에 검색하면 쉽게 찾으실 수 있습니다.",
  },
  {
    type: "img",
    resource: "direct/content/sn/2-3.png",
    imgAlt: "임종 준비 대처 방법",
  },
  { type: "h3", resource: "3. 영정 사진" },
  {
    type: "p",
    resource:
      "끝으로 영정으로 쓸 사진을 챙겨주세요. 상조회사/장례식장을 통해 사진을 인화하고 액자로 제작하는 일은 금방 가능하기 때문에 사진 파일만 챙겨두시면 됩니다. 어느 곳이든 최소한의 보정 작업과 일상복을 양복/한복으로 바꾸는 편집 작업을 해 줍니다. 다만 영정은 사랑하는 사람을 기억하는 마지막 모습일 텐데요, 미리 준비해 두지 않는다면 주민등록증을 스캔-확대 해서 제작하거나 단체 여행에서 찍은 얼굴을 편집해서 제작하고는 합니다. <u>장례식 내내 보는 얼굴이다 보니, 퀄리티가 아쉬워 후회</u>하는 상주님들이 종종 있었습니다.",
  },
  {
    type: "img",
    resource: "direct/content/sn/2-4.png",
    imgAlt: "임종시 필요한 것",
  },
  {
    type: "h2",
    resource: "임종 직후",
  },
  {
    type: "p",
    resource:
      "<u>임종 직후에는 사전에 알아봐 둔 장례 업체(상조회사 또는 장례식장)에 전화하는 것, 사망진단서를 10부 발급받는 것, 딱 2가지만 하시면 됩니다.</u>",
  },
  {
    type: "p",
    resource:
      "사랑하는 분이 돌아가셨다는 소식은 받아들이기 힘듭니다. 정말 힘든 것 같습니다. 오랜 투병을 지켜보면서 마음의 준비를 했더라도, 갑작스럽게 돌아가시게 되었더라도 사망 선고를 듣는 순간에는 정말 머리가 멍해집니다. 최근 소중한 가족과 이별 하게 된 어떤 분께서 이렇게 말씀하실 정도였습니다. '준비를 충분히 했다고 생각했는데도 막상 당일이 되니 손이 떨리고 머리가 하얗게 되어버려서 뭘 어떻게 해야 좋을지 모르겠더라고요'. 그래서 위에서 언급한 2가지만 기억해 두시고, 나머지 모든 것은 장례 업체에 맡기시면 됩니다.",
  },
  {
    type: "img",
    resource: "direct/content/sn/2-5.png",
    imgAlt: "임종 예상 상황",
  },
  { type: "h3", resource: "1. 장례 업체 전화" },
  {
    type: "p",
    resource:
      "임종 소식을 접하게 되면 가장 먼저 장례 업체에 전화하는 것이 필요합니다. ",
  },

  {
    type: "p",
    resource:
      "임종 직후에는 \ni) 장례식장 빈소 확인 및 예약, \nii) 고인 이송 및 앰뷸런스 배차, \nii) 화장 시설 예약\n이 필요한데요, 이 모든 것을 가족 분들이 직접 하기에는 정신도 없거니와 장례 업체 대행하기 때문에 직접 하실 필요가 없습니다.",
  },
  { type: "img", resource: "direct/content/sn/2-6.png", imgAlt: "임종 준비" },
  {
    type: "p",
    resource:
      "먼저 장례식장의 경우 마음속으로 정해둔 곳이 있더라도, 상황에 따라 이용이 불가능한 경우가 빈번하게 발생합니다. 기본적으로 사망은 예측이 불가능하기 때문에 <u>어떤 장례식장도 예약받지 않습니다.</u> 그래서 임종 직후에 사망 소식을 알리며 빈소가 있는지 현황을 전화로 확인해야만 합니다. 빈소 자리가 없다면 시신을 모시는 안치실에 하루 모신 뒤 다음 날 오전부터 빈소 를 차리거나 인근 다른 장례식장을 선정해야 합니다. 그래서 어떨 때는 7~8곳의 장례식장에 전화를 돌리며 겨우 빈소를 예약 하는 경우도 있었던 것 같습니다. 아무튼 장례 업체를 이용한다는 것은 마치 인테리어를 셀프가 아닌 턴키로 맡기는 것과 같기 때문에, <u>업체-장례지도사에게 적극적으로 요청</u>하고 상주님은 판단만 하시는 방식으로 서비스를 이용하는 게 현명하다고 생각합니다.",
  },

  {
    type: "img",
    resource: "direct/content/sn/2-7.png",
    imgAlt: "임종 직전 준비 필요한 것",
  },
  {
    type: "p",
    resource:
      "이렇게 장례식장이 확정되고 나면 고인을 장례식장으로 이송해야 합니다. 부속 시설로 장례식장이 있는 병원의 경우 장례식장 직원이 병실까지 올라와 시신을 수습하고 안치실까지 모시게 되며, 그렇지 않을 경우 병실 혹은 안치실에서 대기하다가 장례를 치를 장례식장까지 앰뷸런스를 통해 이동합니다. 모든 장례 업체가 앰뷸런스를 배차하니 큰 걱정을 하지 않으셔도, 먼 거리를 이동하는 게 아니라면 무료 또는 10만 원 내외의 비용이 발생합니다.",
  },
  {
    type: "p",
    resource:
      "끝으로 화장 예약을 빠르게 해야 하는데요. 화장 예약은 보건복지부에서 운영 중인 e-하늘 장사정보시스템 홈페이지에서만 가능하고, 전국 화장 시설은 1개 빼고 모두 공설로 수요-공급의 불균형이 심합니다. 서울은 2곳(서울시립승화원, 서울추모공원), 경기도는 4곳(수원 연화장, 용인 평온의 숲, 함백산 추모공원(화성), 성남 장례문화사업소, 인천은 1곳(인천가족공원)으로 <u>수도권에 총 7개의 화장 시설이 있는데 원하는 날짜에 화장을 못 하는 경우가 자주 발생합니다.</u> \n\n화장 예약일 및 시각에 맞춰 역순으로 <u>발인 날짜 및 시각, 입관식 등 주요 장례 일정과 빈소 차리는 시간을 정하기 때문에 화장 예약이 확정되는 게 중요합니다.</u> 그리고 이 예약 시스템은 마치 수강 신청과 같아서 선착순으로 예약이 결정되기 때문에 빠르게 해야 하며, <u>이 일 역시 장례지도사가 대행해 주고 있습니다.</u>",
  },
  { type: "img", resource: "direct/content/sn/2-8.png", imgAlt: "임종 준비" },
  { type: "h3", resource: "2. 사망진단서 발급" },
  {
    type: "p",
    resource:
      "사망진단서는 장례 중 장례식장과 화장시설에 각각 1부씩 필수로 제출하며, 장례 이후 사망 신고 및 기타 고인의 금융, 부동산, 차량 등의 재산을 정리하고 행정 업무를 처리하는 과정에서 요구됩니다. 추후 사망진단서가 더 필요할 경우 <u>최초 발급했던 병원에 방문해서 재발급을 받아야만 하므로 미리 넉넉하게 받아두셔야 합니다.</u> 직계 가족만 발급이 가능하며, 사망진단서 발급도 사람이 하는 일이다 보니 고인의 이름/성별/주민등록번호/주소가 정확한지 확인해야 합니다. 발급 비용은 1부당 1~2만 원 선에서 형성되어 있습니다.",
  },
  { type: "img", resource: "direct/content/sn/2-9.png", imgAlt: "임종 준비" },
  {
    type: "p",
    resource:
      "지금까지 임종 전 준비할 것, 임종 직후의 행동 요령을 정리했습니다. 당장은 필요 없겠지만, 만약에라도 장례가 친 상황이 온다면 이 글이 조금이라도 도움이 되었으면 좋겠습니다. 장례에 관해 궁금한 사항이 있다면 댓글로 남겨주세요. 시간이 될 때마다 답변을 드리겠습니다. 다음 연재에서는 장례식 1일 차부터 3일 차까지, 일자별 주요 절차와 상세 내용을 정리할 계획 입니다.",
  },
];
